import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  startAt,
  limit,
  setDoc,
  deleteDoc,
  updateDoc,
  QueryConstraint,
  CollectionReference,
  DocumentReference,
  Timestamp,
  collectionData,
  collectionChanges,
} from '@angular/fire/firestore';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AppInjector } from 'src/app/app.injector.module';

import { LogService } from '../../helpers/log.service';
import { PaginationResult } from '../../models/pagination-result.interface';
import { FirestoreEntity } from '../../base/firestore-entity';

import { ToastService } from './../api/toast.service';

export class BaseService<T extends FirestoreEntity> {
  protected documentPath: string;
  protected logService: LogService;
  private getAllCached$: Observable<T[]>;

  constructor(
    protected db: Firestore,
    protected toastService: ToastService
  ) {
    this.logService = AppInjector.get(LogService);
  }

  getById(id: string): Observable<T> {
    const docRef = doc(this.db, `${this.documentPath}/${id}`);
    return from(getDoc(docRef)).pipe(
      tap(() => this.logService.info(`[getById] path: ${this.documentPath}, id: ${id}`)),
      map((snapshot) => this.convertFirestoreTimeStamps(snapshot.data() as T))
    );
  }

  getSnapshotChanges(queryConstraints: QueryConstraint[] = []): Observable<T[]> {
    const colRef = collection(this.db, this.documentPath) as CollectionReference<T>;
    const q = query(colRef, ...queryConstraints);

    return from(getDocs(q)).pipe(
      tap(() => this.logService.info(`[getSnapshotChanges] path: ${this.documentPath}`)),
      map((snapshot) => snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as T)))
    );
  }

  getSnapshotChangesWithPagination(queryConstraints: QueryConstraint[] = []): Observable<PaginationResult<T>> {
    const colRef = collection(this.db, this.documentPath) as CollectionReference<T>;
    const q = query(colRef, ...queryConstraints);

    return from(getDocs(q)).pipe(
      tap(() => this.logService.info(`[getSnapshotChangesWithPagination] path: ${this.documentPath}`)),
      map((snapshot) => {
        const data = snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as T));
        const pResult: PaginationResult<T> = {
          items: data,
          firstDoc: snapshot.docs[0],
          lastDoc: snapshot.docs[snapshot.docs.length - 1],
        };
        return pResult;
      }),
      catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught))
    );
  }

  getAll(): Observable<T[]> {
    this.logService.info('[getAll]', this.documentPath);
    const colRef = collection(this.db, this.documentPath) as CollectionReference<T>;
    return from(getDocs(colRef)).pipe(
      tap(() => this.logService.info(`[getAll] path: ${this.documentPath}`)),
      map((snapshot) => snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as T))),
      catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught))
    );
  }

  getAllCached(): Observable<T[]> {
    if (!this.getAllCached$) {
      this.logService.warning('getAllCached');
      const colRef = collection(this.db, this.documentPath) as CollectionReference<T>;
      this.getAllCached$ = from(getDocs(colRef)).pipe(
        tap(() => this.logService.info(`[getAll] path: ${this.documentPath}`)),
        map((snapshot) => snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as T))),
        catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught)),
        shareReplay({ refCount: true, bufferSize: 1 })
      );
    } else {
      this.logService.success('getAllCached');
    }
    return this.getAllCached$;
  }

  getAllWithPagination(startAfterDoc: T | null, limitCount: number): Observable<PaginationResult<T>> {
    const colRef = collection(this.db, this.documentPath) as CollectionReference<T>;
    let q;

    if (startAfterDoc) {
      q = query(colRef, startAt(startAfterDoc), limit(limitCount));
    } else {
      q = query(colRef, limit(limitCount));
    }

    return from(getDocs(q)).pipe(
      tap(() => this.logService.info(`[getAllWithPagination] path: ${this.documentPath}`)),
      map((snapshot) => {
        const data = snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as T));
        const pResult: PaginationResult<T> = {
          items: data,
          firstDoc: snapshot.docs[0],
          lastDoc: snapshot.docs[snapshot.docs.length - 1],
        };
        return pResult;
      }),
      catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught))
    );
  }

  getWithQuery(queryConstraints: QueryConstraint[] = []): Observable<T[]> {
    const colRef = collection(this.db, this.documentPath) as CollectionReference<T>;
    const q = query(colRef, ...queryConstraints);
    // ACTIE DIE LUISTERT NAAR CHANGES
    return collectionData(q).pipe(
      map((changes) => {
        return changes.map((entity) => {
          // console.log('veranderd: ', entity);
          return this.convertFirestoreTimeStamps(entity);
        });
      }),
      catchError((err: any, caught: Observable<any>) => {
        return this.generalErrorHandler(err, caught);
      })
    );
    // EEN MALIGE ACTIE
    // return from(getDocs(q)).pipe(
    //   tap(() => this.logService.info(`[getWithQuery] path: ${this.documentPath}`)),
    //   map((snapshot) => snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as T))),
    //   catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught))
    // );
  }

  createNewId(): string {
    this.logService.info('[createNewId] Generating a new UUID');
    return uuidv4();
  }

  /**
   * Creates an ID which consists of 40 characters.
   * chances of guessing a correct ID is:
   *
   * 62 (alphanumeric upper- and lowercase, 0-9) ^ 40 = 4,962123624593670669143665801957e+71 possibilities
   *
   * If we generate 1 million ID's, the chance of guessing a correct one would be:
   *
   * (1.000.000 / 4,962123624593670669143665801957e+71) * 100 = 2,0152661957951242629177387777471e-64 %
   *
   */
  createNewBigId(): string {
    this.logService.info('[createNewBigId] Generating a new 40-character ID');
    const part1 = uuidv4().replace(/-/g, ''); // Verwijder streepjes uit UUID
    const part2 = uuidv4().replace(/-/g, '');
    return (part1 + part2).substring(0, 40); // Combineer en beperk tot 40 tekens
  }

  create(data: T): Promise<void> {
    if (!data.documentId) {
      data.documentId = uuidv4(); // Gebruik uuid om een unieke ID te genereren
    }

    // Metadata toevoegen (indien nodig)
    // data.createdById = this.authService.user.uid;
    // data.createdByName = this.authService.user.displayName;
    // data.createdDate = new Date();

    const docRef = doc(this.db, `${this.documentPath}/${data.documentId}`);
    return setDoc(docRef, data).then(() => {
      this.logService.success('[create] ', this.documentPath);
    });
  }

  delete(id: string): Promise<void> {
    this.logService.danger('[delete] ', this.documentPath);
    const docRef = doc(this.db, `${this.documentPath}/${id}`);
    return deleteDoc(docRef);
  }

  update(data: T): Promise<void> {
    this.logService.success('[update] ', this.documentPath);
    this.logService.info('[update data] ', data);

    // Metadata toevoegen (indien nodig)
    // data.modifiedById = this.authService.user.uid;
    // data.modifiedByName = this.authService.user.displayName;
    // data.modifiedDate = new Date();

    const docRef = doc(this.db, `${this.documentPath}/${data.documentId}`);
    // return updateDoc(docRef, { ...data });
    return updateDoc(docRef, { ...(data as any) }); // Gebruik een typecast
  }

  getAllFromSubCollection<K>(parentId: string, subCollection: string): Observable<K[]> {
    const subColRef = collection(this.db, `${this.documentPath}/${parentId}/${subCollection}`) as CollectionReference<K>;
    return from(getDocs(subColRef)).pipe(
      map((snapshot) => snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as K))),
      catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught))
    );
  }

  getFromSubCollectionWithQuery<K>(parentId: string, subCollection: string, queryConstraints: QueryConstraint[] = []): Observable<K[]> {
    const subColRef = collection(this.db, `${this.documentPath}/${parentId}/${subCollection}`) as CollectionReference<K>;

    const q = query(subColRef, ...queryConstraints);

    return from(getDocs(q)).pipe(
      map((snapshot) => snapshot.docs.map((doc) => this.convertFirestoreTimeStamps(doc.data() as K))),
      catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught))
    );
  }

  addToSubcollection<K extends FirestoreEntity>(parentId: string, subCollection: string, data: K): Promise<void> {
    if (!data.documentId) {
      data.documentId = uuidv4(); // Gebruik uuid om een unieke ID te genereren
    }

    const subColDocRef = doc(this.db, `${this.documentPath}/${parentId}/${subCollection}/${data.documentId}`);

    return setDoc(subColDocRef, data);
  }

  removeFromSubcollection<K extends FirestoreEntity>(parentId: string, subCollection: string, id: string): Promise<void> {
    const subColDocRef = doc(this.db, `${this.documentPath}/${parentId}/${subCollection}/${id}`);

    return deleteDoc(subColDocRef);
  }

  updateSubCollectionDoc<K extends FirestoreEntity>(parentId: string, subCollection: string, data: K): Promise<void> {
    this.logService.success('[updateSubCollectionDoc] Updating document in subcollection');
    this.logService.info('[updateSubCollectionDoc data] ', data);

    const subColDocRef = doc(this.db, `${this.documentPath}/${parentId}/${subCollection}/${data.documentId}`);

    return setDoc(subColDocRef, data, { merge: true });
  }

  getByIdFromSubCollection<K extends FirestoreEntity>(parentId: string, subCollection: string, id: string): Observable<K> {
    const subColDocRef = doc(this.db, `${this.documentPath}/${parentId}/${subCollection}/${id}`) as DocumentReference<K>;

    return from(getDoc(subColDocRef)).pipe(
      map((snapshot) => {
        return this.convertFirestoreTimeStamps(snapshot.data() as K);
      }),
      catchError((err: any, caught: Observable<any>) => this.generalErrorHandler(err, caught))
    );
  }

  public convertFirestoreTimeStamps<K>(entity: K): K {
    if (entity) {
      Object.keys(entity).forEach((key) => {
        if (entity[key] instanceof Timestamp) {
          entity[key] = (entity[key] as Timestamp).toDate();
        }

        if (typeof entity[key] === 'object' && entity[key] !== null) {
          this.convertFirestoreTimeStamps(entity[key]);
        }
      });
    }
    return entity;
  }

  private generalErrorHandler(err: any, caught: Observable<any>): Observable<never> {
    this.logService.danger(`[base.service] generalErrorHandler(): ${this.documentPath} \n${err}`);
    this.toastService.showError(err);
    return throwError(() => err); // Gebruik een factory-functie
  }
}
